import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators'
import { logger } from '@/utils/logger'

import { ApiTaCm030 } from '@/endpoints/ApiTaCm030'

export namespace MonthlistType {
  /**
   * レポートタイプ
   * 実力テスト: 0
   * 月例テスト: 1
   * 学習診断レポート: 2
   * マンスリーチェック: 3
   * T提出課題：4
   */
  export const reportType = {
    abilityTest: 0,
    monthlyTest: 1,
    learningReport: 2,
    monthlyCheck: 3,
    assignmentTest: 4,
  } as const
  export type ReportTypes = typeof reportType[keyof typeof reportType]

  export function convertReportTypeToNumber(type: ReportTypes): number {
    switch (type) {
      case reportType.abilityTest:
        return 0
      case reportType.monthlyTest:
        return 1
      case reportType.learningReport:
        return 2
      case reportType.monthlyCheck:
        return 3
      case reportType.assignmentTest:
        return 4
      default:
        throw new Error('unexpected type')
    }
  }

  export function convertNumberToReportType(num: number): ReportTypes {
    switch (num) {
      case 0:
        return reportType.abilityTest
      case 1:
        return reportType.monthlyTest
      case 2:
        return reportType.learningReport
      case 3:
        return reportType.monthlyCheck
      case 4:
        return reportType.assignmentTest
      default:
        throw new Error('unexpected number')
    }
  }

  /**
   * テストタイプ
   * レポートタイプが実力テスト => 1, 2, 3, 4 のいづれか
   * レポートタイプが実力テスト以外 => 0 のみ
   * T提出課題 => 21(25年度より新設)
   * なし: 0
   * Tコース: 1
   * 定着度・力試し: 2
   * 志望校判断: 3
   * 志望校合格力判断: 4
   * T提出課題: 21
   */
  export const testType = {
    none: 0,
    courseT: 1,
    fixability: 2,
    forecastAchievement: 3,
    passingAbilityDiagnosis: 4,
    assignmentTest: 21,
  } as const
  export type TestTypes = typeof testType[keyof typeof testType]

  export function convertTestTypeToNumber(type: TestTypes): number {
    switch (type) {
      case testType.none:
        return 0
      case testType.courseT:
        return 1
      case testType.fixability:
        return 2
      case testType.forecastAchievement:
        return 3
      case testType.passingAbilityDiagnosis:
        return 4
      case testType.assignmentTest:
        return 21
      default:
        throw new Error('unexpected type')
    }
  }

  export function convertNumberToTestType(num: number): TestTypes {
    switch (num) {
      case 0:
        return testType.none
      case 1:
        return testType.courseT
      case 2:
        return testType.fixability
      case 3:
        return testType.forecastAchievement
      case 4:
        return testType.passingAbilityDiagnosis
      case 21:
        return testType.assignmentTest
      default:
        throw new Error('unexpected number')
    }
  }
}

// State types
interface ReportListObject {
  reportId: string
  memNum: string
  grade: number
  fiscalYear: number
  attendedMonth: number
  testCountPerMonth: number
  isRead?: boolean
  testType: number
}

type MonthlyReportList = {
  reportType: MonthlistType.ReportTypes
  reportList: ReportListObject[]
}

export type MonthlyReportListState = MonthlyReportList | null

// State action parameter
interface ActionParamMonthReportList {
  reportType: MonthlistType.ReportTypes
  offset: string
  limit: number
}

@Module({
  stateFactory: true,
  namespaced: true,
  name: 'apis/Monthlist',
})
export default class Monthlist extends VuexModule {
  // State
  private monthReportList_: MonthlyReportListState = null
  private hasMoreReport_: boolean = true

  // Getter
  get monthlyReportList(): MonthlyReportListState {
    return this.monthReportList_
  }

  get hasMoreReport(): boolean {
    return this.hasMoreReport_
  }

  @Mutation
  updateMonthlyReportList(newState: MonthlyReportList) {
    if (this.monthReportList_ === null) {
      this.monthReportList_ = newState
      return
    }
    this.monthReportList_.reportList = this.monthReportList_.reportList.concat(newState.reportList)
  }

  @Mutation
  clearMonthlyReportList() {
    this.monthReportList_ = null
  }

  @Mutation
  updateHasMoreReport(flg: boolean) {
    this.hasMoreReport_ = flg
  }

  @Mutation
  clearHasMoreReport() {
    this.hasMoreReport_ = true
  }

  @Action
  clear() {
    this.context.commit('clearMonthlyReportList')
    this.context.commit('clearHasMoreReport')
  }

  @Action({ rawError: true })
  async refreshMonthlyReportList(param: ActionParamMonthReportList) {
    logger.trace('in: refreshMonthlyReportList')
    const api = new ApiTaCm030.Api()

    this.context.commit('updateHasMoreReport', false)

    const result = await api.get<MonthlyReportListState>(
      {
        query: {
          reportType: MonthlistType.convertReportTypeToNumber(param.reportType),
          offset: param.offset,
          limit: param.limit,
        },
      },
      // success
      (data, _) => {
        return {
          reportType: MonthlistType.convertNumberToReportType(data.reportType),
          reportList: data.reportList.map((list): ReportListObject => {
            return {
              reportId: list.reportId,
              memNum: list.memNum,
              grade: list.grade,
              fiscalYear: list.fiscalYear,
              attendedMonth: list.attendedMonth,
              testCountPerMonth: list.testCountPerMonth,
              isRead: list.isRead,
              testType: MonthlistType.convertNumberToTestType(list.testType),
            }
          }),
        }
      },
      // error
      (data, status) => {
        logger.error(status, data)
        return {
          reportType: param.reportType,
          reportList: [],
        }
      }
    )

    if (result === null || result === undefined) {
      this.context.commit('updateHasMoreReport', true)
      return
    }

    this.context.commit('updateHasMoreReport', result.reportList.length === param.limit)
    this.context.commit('updateMonthlyReportList', result)
  }
}
